/**
 * Signage player state machine.
 *
 * Direct port of apps/signage-tv/lib/state/device_state.dart. Keeping the
 * two machines semantically identical is the cheapest way to ensure the
 * two clients behave the same way on the wire (same state → same backend
 * contract).
 *
 * Pure functions only — no timers, no I/O. The controller in
 * src/state/controller.ts wires this machine to fetch + setInterval.
 */

export type DeviceState =
  | 'boot'
  | 'unpaired'
  | 'rendering'
  | 'offline'
  | 'stale'
  | 'unbound'
  | 'updating';

export type DeviceEvent =
  | { type: 'bootstrapComplete'; hasToken: boolean }
  | { type: 'pairingStarted' }
  | { type: 'codeIssued' }
  | { type: 'codeExpired' }
  | { type: 'paired' }
  | { type: 'contentReceived' }
  | { type: 'contentFailed' }
  | { type: 'networkLost' }
  | { type: 'networkRestored' }
  | { type: 'staleBreached' }
  | { type: 'unboundBreached' }
  | { type: 'commandReceived'; commandType: 'refresh' | 'force_mode' | 'reboot' | 'unpair' }
  | { type: 'tokenRevoked' }
  | { type: 'upgradeRequired' };

export interface TransitionResult {
  readonly state: DeviceState;
  readonly changed: boolean;
}

/**
 * Compute the next state given the current state and an event.
 *
 * Unknown (state, event) pairs return `{state: current, changed: false}` —
 * the machine is permissive by design; the controller uses this property
 * to decide whether to re-render or persist.
 */
export function transition(current: DeviceState, event: DeviceEvent): TransitionResult {
  const next = nextState(current, event);
  return { state: next, changed: next !== current };
}

function nextState(s: DeviceState, e: DeviceEvent): DeviceState {
  switch (e.type) {
    case 'bootstrapComplete':
      // Boot picks the entry state based on whether storage has a token.
      return s === 'boot' ? (e.hasToken ? 'rendering' : 'unpaired') : s;

    case 'pairingStarted':
      return s === 'boot' || s === 'unbound' ? 'unpaired' : s;

    case 'codeIssued':
      // No state change — keeps us in 'unpaired' while polling.
      return s;

    case 'codeExpired':
      return s === 'unpaired' ? 'unpaired' : s;

    case 'paired':
      return s === 'unpaired' ? 'rendering' : s;

    case 'contentReceived':
      // Any of (rendering|offline|stale) → rendering (fresh success).
      if (s === 'offline' || s === 'stale') return 'rendering';
      return s;

    case 'contentFailed':
      // First-failure doesn't move us off rendering — the controller batches
      // transient errors. networkLost is the explicit edge.
      return s;

    case 'networkLost':
      return s === 'rendering' ? 'offline' : s;

    case 'networkRestored':
      return s === 'offline' ? 'rendering' : s;

    case 'staleBreached':
      return s === 'offline' ? 'stale' : s;

    case 'unboundBreached':
      return s === 'stale' ? 'unbound' : s;

    case 'commandReceived':
      if (e.commandType === 'unpair') {
        // Controller wipes + emits pairingStarted; machine just leaves
        // rendering so we surface the transition to observers.
        return s === 'rendering' || s === 'offline' || s === 'stale' ? 'unbound' : s;
      }
      // refresh / force_mode / reboot do not themselves change state —
      // controller handles side effects and issues contentReceived
      // after the refresh cycle completes.
      return s;

    case 'tokenRevoked':
      // Any paired state → unbound → controller wipes → unpaired.
      return s === 'rendering' || s === 'offline' || s === 'stale' ? 'unbound' : s;

    case 'upgradeRequired':
      // Terminal from the player's POV — loops pause until page reload.
      return 'updating';
  }
}

/** True when the state represents a paired client still rendering content. */
export function isPaired(s: DeviceState): boolean {
  return s === 'rendering' || s === 'offline' || s === 'stale';
}

/** True when the controller should run background loops (heartbeat/content/commands). */
export function shouldRunLoops(s: DeviceState): boolean {
  return isPaired(s);
}
